import { useNode } from "@craftjs/core";
import AddIcon from "@mui/icons-material/Add";
import { Box, Stack } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import TextView from "../../../TextView";
import { QuadStepper } from "../../BlockEditorPane/controls";
import { CHART_TYPES } from "./Graph";
import GraphDataPointRow from "./GraphDataPointRow";
import { MAX_POINTS, PRESET_REFS, fieldStyle } from "./graphSettingsShared";

// visually-hidden pattern this app already uses for aria-live announcers
// (see WheelGallery/WheelListing/TabComponents/List.jsx's `liveCount`).
const srOnlyStyle = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

const ChartTypeChip = ({ active, label, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-pressed={active}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      cursor: "pointer",
      userSelect: "none",
      px: 1.25,
      py: 0.5,
      borderRadius: "16px",
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      fontFamily: "'DM Sans', sans-serif",
      border: active ? "1px solid #6D5EF6" : "1px solid #E4E7EC",
      backgroundColor: active ? "#6D5EF6" : "#FFFFFF",
      color: active ? "#FFFFFF" : "#475467",
      "&:focus-visible": { boxShadow: "0 0 0 3px rgba(109,94,246,0.35)" },
    }}
  >
    {label}
  </Box>
);

const GraphSettings = () => {
  const {
    actions: { setProp },
    title,
    chartType,
    data,
    margin,
  } = useNode((node) => ({
    title: node.data.props.title,
    chartType: node.data.props.chartType,
    data: node.data.props.data,
    margin: node.data.props.margin,
  }));

  const rows = Array.isArray(data) ? data : [];
  // gordon-0915-graph-block-remove-scatter-chart-type: Scatter is gone from
  // CHART_TYPES, but a block saved with the old chartType: "scatter" must
  // still show a selected chip — it renders as Line (see GraphChart.jsx), so
  // show Line as selected here too. Purely a display concern: setProp is
  // never called just from this normalization, so the saved prop is
  // untouched until the user actively picks a new type.
  const activeChartType = chartType === "scatter" ? "line" : chartType || "bar";

  const rowHandleRefs = useRef([]);
  const pendingFocusIndex = useRef(null);
  const [announcement, setAnnouncement] = useState("");

  const setTitle = (value) => setProp((props) => (props.title = value.slice(0, 60)));
  const setChartType = (key) => setProp((props) => (props.chartType = key));
  const setMarginSide = (side, value) =>
    setProp((props) => {
      props.margin = { top: 0, right: 0, bottom: 0, left: 0, ...props.margin, [side]: value };
    });

  const updateRow = (index, key, value) =>
    setProp((props) => {
      if (!Array.isArray(props.data)) props.data = [];
      if (!props.data[index]) props.data[index] = { label: "", value: 0, color: PRESET_REFS[0] };
      props.data[index][key] = value;
    });

  const addRow = () =>
    setProp((props) => {
      if (!Array.isArray(props.data)) props.data = [];
      if (props.data.length >= MAX_POINTS) return;
      const nextColor = PRESET_REFS[props.data.length % PRESET_REFS.length];
      props.data.push({ label: `Item ${props.data.length + 1}`, value: 10, color: nextColor });
    });

  const removeRow = (index) =>
    setProp((props) => {
      if (Array.isArray(props.data)) props.data.splice(index, 1);
    });

  // gordon-0915-graph-block-data-points-drag-reorder: order lives purely in
  // `data`'s array order (Graph.jsx/GraphChart.jsx already render/chart in
  // that order verbatim), so a splice-based move is all persistence needs —
  // craft.js serializes/deserializes node.data.props.data as-is.
  const moveRow = (fromIndex, toIndex, opts = {}) => {
    if (toIndex < 0 || toIndex >= rows.length || fromIndex === toIndex) return;
    const movedLabel = rows[fromIndex]?.label || "Untitled data point";
    setProp((props) => {
      if (!Array.isArray(props.data)) return;
      const [moved] = props.data.splice(fromIndex, 1);
      props.data.splice(toIndex, 0, moved);
    });
    setAnnouncement(`${movedLabel} moved to position ${toIndex + 1} of ${rows.length}`);
    if (opts.viaKeyboard) pendingFocusIndex.current = toIndex;
  };

  useEffect(() => {
    if (pendingFocusIndex.current == null) return;
    const idx = pendingFocusIndex.current;
    pendingFocusIndex.current = null;
    rowHandleRefs.current[idx]?.focus();
  }, [data]);

  return (
    <Box sx={{ p: 0.5 }}>
      {/* Title (optional) */}
      <Box sx={{ mb: 2 }}>
        <TextView variant="subtitle" style={{ marginBottom: 6 }}>
          Title (optional)
        </TextView>
        <input
          style={fieldStyle}
          value={title || ""}
          placeholder="Chart title"
          onChange={(e) => setTitle(e.target.value)}
          data-cy="graph-title-input"
        />
      </Box>

      {/* Chart type */}
      <Box sx={{ mb: 2 }}>
        <TextView variant="subtitle" style={{ marginBottom: 6 }}>
          Chart type
        </TextView>
        <Stack direction="row" flexWrap="wrap" gap={0.75} data-cy="graph-type-picker">
          {CHART_TYPES.map((t) => (
            <ChartTypeChip
              key={t.key}
              label={t.label}
              active={activeChartType === t.key}
              onClick={() => setChartType(t.key)}
            />
          ))}
        </Stack>
      </Box>

      {/* Data points */}
      <Box sx={{ mb: 1 }}>
        <TextView variant="subtitle" style={{ marginBottom: 6 }}>
          Data points
        </TextView>
        {/* aria-live announcement (drag or keyboard reorder) — visually hidden */}
        <Box aria-live="polite" role="status" sx={srOnlyStyle} data-cy="graph-reorder-announcer">
          {announcement}
        </Box>
        <DndProvider backend={HTML5Backend}>
          <Stack
            spacing={1.25}
            data-cy="graph-data-rows"
            role="list"
            aria-label="Data points"
          >
            {rows.map((row, i) => (
              <GraphDataPointRow
                key={i}
                ref={(el) => (rowHandleRefs.current[i] = el)}
                row={row}
                index={i}
                total={rows.length}
                moveRow={moveRow}
                onLabelChange={(value) => updateRow(i, "label", value)}
                onValueChange={(value) => updateRow(i, "value", value)}
                onColorChange={(value) => updateRow(i, "color", value)}
                onRemove={() => removeRow(i)}
                disableRemove={rows.length <= 1}
              />
            ))}
          </Stack>
        </DndProvider>

        <Box
          role="button"
          tabIndex={0}
          onClick={addRow}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addRow();
            }
          }}
          aria-disabled={rows.length >= MAX_POINTS}
          data-cy="graph-add-row"
          sx={{
            mt: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            cursor: rows.length >= MAX_POINTS ? "not-allowed" : "pointer",
            opacity: rows.length >= MAX_POINTS ? 0.5 : 1,
            border: "1px dashed #6D5EF6",
            borderRadius: "8px",
            py: 1,
            color: "#6D5EF6",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            "&:focus-visible": { boxShadow: "0 0 0 3px rgba(109,94,246,0.35)" },
          }}
        >
          <AddIcon fontSize="small" /> Add data point
        </Box>
      </Box>

      {/* fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458:
          reuses the SAME QuadStepper Text/Image already use for their
          Padding/Margin control (BlockEditorPane/controls.jsx), with its
          existing fullWidth prop to hide Left/Right (Gordon ruling:
          hideHorizontal for Table and Graph only — Graph already pads its
          own content horizontally, see Graph.jsx). */}
      <Box sx={{ mb: 1 }}>
        <QuadStepper label="Margin" value={margin} onChange={setMarginSide} fullWidth />
      </Box>
    </Box>
  );
};

export default GraphSettings;
